export default function AccidentCriteria() {
  return (
    <section id="accident-criteria" className="py-32 bg-gradient-to-br from-white via-[#F0F9FF] to-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#0EA5E9]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#0EA5E9]/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <span className="inline-block px-5 py-2 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-semibold rounded-full mb-6">
            ACCIDENT CRITERIA
          </span>
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            사고의 3가지 구분
          </h2>
          <p>
            장기요양기관에서 발생하는 사고를 명확히 구분하여<br />
            정확한 책임 판단과 합리적인 보상을 제공합니다
          </p>
        </div>

        {/* Accident Types */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mb-20">
          {/* Type 1 */}
          <div className="group bg-white rounded-3xl shadow-xl overflow-hidden border-2 border-[#0EA5E9]/20 hover:shadow-2xl hover:-translate-y-3 transition-all duration-500 flex flex-col">
            <div className="relative h-56 overflow-hidden">
              <img 
                src="https://public.readdy.ai/ai/img_res/0a88079a7c914dddd2d87f627e64914c.jpg"
                alt="전문인 또는 시설 과실 사고"
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="inline-block px-4 py-2 bg-red-500 text-white text-sm font-semibold rounded-full mb-2 whitespace-nowrap">
                  시설 과실
                </div>
                <h3 className="text-2xl font-bold text-white whitespace-nowrap">전문인 또는 시설 과실 사고</h3>
              </div>
            </div>
            <div className="p-8 flex flex-col flex-1">
              <p>
                시설 또는 종사자의 명백한 과실로 인해 발생한 사고
              </p>
              
              <div className="space-y-3 mb-6 mt-auto">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p>부주의한 관리로 인한 낙상</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p>방치로 인한 욕창 발생</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p>투약 오류 및 의료 과실</p>
                </div>
              </div>

              <div className="bg-red-50 rounded-2xl p-5 border border-red-100">
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-alert-line text-red-500 text-xl"></i>
                  <span className="text-sm font-bold text-red-700 whitespace-nowrap">보험 처리</span>
                </div>
                <p>
                  시설의 법적 배상책임이 인정되어 보험금이 지급됩니다
                </p>
              </div>
            </div>
          </div>

          {/* Type 2 */}
          <div className="group bg-white rounded-3xl shadow-xl overflow-hidden border-2 border-[#0EA5E9]/20 hover:shadow-2xl hover:-translate-y-3 transition-all duration-500 flex flex-col">
            <div className="relative h-56 overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=Korean%20elderly%20grandmother%20peacefully%20sleeping%20in%20comfortable%20bed%20at%20modern%20Korean%20nursing%20home%20facility%20with%20Korean%20female%20nurse%20quietly%20monitoring%20health%20condition%20showing%20natural%20aging%20and%20rest%20with%20gentle%20morning%20sunlight%20through%20window%20clean%20white%20bedding%20and%20calm%20serene%20atmosphere%20in%20professional%20Korean%20long-term%20care%20center%20with%20medical%20equipment%20and%20caring%20healthcare%20environment&width=600&height=400&seq=accident-natural-korean-elderly-rest-new-image-042&orientation=landscape"
                alt="자연 발생 사고"
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="inline-block px-4 py-2 bg-green-500 text-white text-sm font-semibold rounded-full mb-2 whitespace-nowrap">
                  자연 발생
                </div>
                <h3 className="text-2xl font-bold text-white whitespace-nowrap">자연 발생 사고</h3>
              </div>
            </div>
            <div className="p-8 flex flex-col flex-1">
              <p>
                시설의 과실 없이 자연적으로 발생한 사고
              </p>
              
              <div className="space-y-3 mb-6 mt-auto">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p>기저질환 악화</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p>노화로 인한 자연스러운 건강 악화</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p>예측 불가능한 돌발 상황</p>
                </div>
              </div>

              <div className="bg-green-50 rounded-2xl p-5 border border-green-100">
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-checkbox-circle-line text-green-500 text-xl"></i>
                  <span className="text-sm font-bold text-green-700 whitespace-nowrap">보험 처리</span>
                </div>
                <p>
                  시설의 과실이 없어 보험금 지급 대상이 아닙니다
                </p>
              </div>
            </div>
          </div>

          {/* Type 3 */}
          <div className="group bg-white rounded-3xl shadow-xl overflow-hidden border-2 border-[#0EA5E9]/20 hover:shadow-2xl hover:-translate-y-3 transition-all duration-500 flex flex-col">
            <div className="relative h-56 overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=Korean%20medical%20professional%20carefully%20reviewing%20accident%20investigation%20documents%20and%20medical%20records%20with%20laptop%20in%20modern%20Korean%20office%20environment%20showing%20detailed%20analytical%20work%20and%20professional%20judgment%20process%20with%20Korean%20healthcare%20specialist%20examining%20case%20files%20and%20evidence%20materials%20in%20bright%20professional%20workspace%20with%20focused%20concentration&width=600&height=400&seq=accident-type3-korean-medical-analysis-015&orientation=landscape"
                alt="판단 필요 사고"
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="inline-block px-4 py-2 bg-amber-500 text-white text-sm font-semibold rounded-full mb-2 whitespace-nowrap">
                  판단 필요
                </div>
                <h3 className="text-2xl font-bold text-white whitespace-nowrap">판단이 필요한 사고</h3>
              </div>
            </div>
            <div className="p-8 flex flex-col flex-1">
              <p>
                시설 과실 여부를 판단하기 위해 조사가 필요한 사고
              </p>
              
              <div className="space-y-3 mb-6 mt-auto">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p>원인이 불명확한 낙상</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p>복합적 요인의 사고</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p>과실 여부 논란이 있는 경우</p>
                </div>
              </div>

              <div className="bg-amber-50 rounded-2xl p-5 border border-amber-100">
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-search-line text-amber-500 text-xl"></i>
                  <span className="text-sm font-bold text-amber-700 whitespace-nowrap">보험 처리</span>
                </div>
                <p>
                  전담 손해사정사의 정밀 조사 후 책임 여부를 판단합니다
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Process Flow */}
        <div className="bg-emerald-600 text-white rounded-3xl p-8 shadow-xl">
          <div className="text-center text-2xl md:text-3xl font-bold mb-6">사고 판단 프로세스</div>
          <div className="bg-white text-gray-800 rounded-2xl p-6 md:p-8">
            <div className="flex items-start gap-3 mb-6">
              <i className="ri-search-eye-line text-3xl text-emerald-600 flex-shrink-0"></i>
              <p className="font-bold text-base md:text-lg">전담 손해사정사가 체계적으로 사고를 판단합니다</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-emerald-50 rounded-xl p-5 border-2 border-emerald-200">
                <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mb-3 mx-auto">
                  <span className="text-xl font-bold text-white">1</span>
                </div>
                <h4 className="text-center font-semibold text-gray-900 mb-2">사고 접수</h4>
                <p className="text-center text-sm text-gray-700">신속한 사고 신고 및 접수</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-5 border-2 border-emerald-200">
                <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mb-3 mx-auto">
                  <span className="text-xl font-bold text-white">2</span>
                </div>
                <h4 className="text-center font-semibold text-gray-900 mb-2">현장 조사</h4>
                <p className="text-center text-sm text-gray-700">전문가의 정밀 현장 조사</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-5 border-2 border-emerald-200">
                <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mb-3 mx-auto">
                  <span className="text-xl font-bold text-white">3</span>
                </div>
                <h4 className="text-center font-semibold text-gray-900 mb-2">책임 판단</h4>
                <p className="text-center text-sm text-gray-700">공정한 과실 여부 판단</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-5 border-2 border-emerald-200">
                <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mb-3 mx-auto">
                  <span className="text-xl font-bold text-white">4</span>
                </div>
                <h4 className="text-center font-semibold text-gray-900 mb-2">보상 처리</h4>
                <p className="text-center text-sm text-gray-700">신속하고 정확한 보상</p>
              </div>
            </div>
            <p className="mt-6 text-center text-sm md:text-base text-gray-700">
              <strong>체계적인 4단계 프로세스</strong>를 통해 공정하고 신속한 사고 처리를 보장합니다.<br />
              전문성과 투명성을 바탕으로 시설과 전문인 모두를 보호합니다.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
